import {
  createContext,
  PropsWithChildren,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useRef,
} from "react";

import { HighLevelContextState, PlanList, Plan } from "./types";
import { highlevelContextReducer } from "./utils";

const initialState: HighLevelContextState = {
  client: null,
  isLoading: false,
  error: null,
  getPlanList: async () => ({ items: [], total: 0 }),
  getPlanById: async () => null,
  createCheckoutSession: async () => null,
  clearPlanCache: () => undefined,
};

export const HighLevelContext = createContext(initialState);

// Move CACHE_DURATION outside component to prevent recreation
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const HighLevelContextProvider = ({ children }: PropsWithChildren) => {
  const [state, setState] = useReducer(highlevelContextReducer, initialState);
  const planListCache = useRef<{ data: PlanList; timestamp: number } | null>(null);
  const isLoadingPlans = useRef(false);

  useEffect(() => {
    const init = async () => {
      // Initialize HighLevel client configuration
      // Use OLD API key for membership plans (products/pricing API)
      // This is different from the backend key used for contacts (new key)
      // The old key: pit-42275d16-21d0-45ec-8a60-a618029ac0ff
      // The new key (for contacts): pit-bcba6ea9-e16f-45e5-a96c-bcb7903592c1
      const client = {
        apiKey: process.env.REACT_APP_HIGHLEVEL_MEMBERSHIPS_API_KEY || 'pit-42275d16-21d0-45ec-8a60-a618029ac0ff',
        locationId: process.env.REACT_APP_HIGHLEVEL_LOCATION_ID || 'iatk4RAPtMOPMhPoQP7M',
        baseUrl: 'https://services.leadconnectorhq.com',
      };

      setState({
        type: "SET_CLIENT",
        payload: client,
      });
    };
    init();
  }, []);

  const getPlanList = useCallback(async (): Promise<PlanList> => {
    if (!state.client) {
      return { items: [], total: 0 };
    }

    // Check cache first
    const now = Date.now();
    if (planListCache.current && (now - planListCache.current.timestamp) < CACHE_DURATION) {
      return planListCache.current.data;
    }

    // Prevent multiple simultaneous API calls
    if (isLoadingPlans.current) {
      // Wait for the current request to complete
      while (isLoadingPlans.current) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      // Check cache again after waiting
      if (planListCache.current && (Date.now() - planListCache.current.timestamp) < CACHE_DURATION) {
        return planListCache.current.data;
      }
    }

    isLoadingPlans.current = true;
    setState({ type: "SET_LOADING", payload: true });

    try {
      // Get product IDs from environment variables with their plan types
      const productConfig = [
        {
          productId:
            process.env.REACT_APP_HL_DESIGNER_SMART_GUILD_PRODUCT_ID ||
            process.env.REACT_APP_HL_DESIGN_PRODUCT_ID,
          priceId:
            process.env.REACT_APP_HL_DESIGNER_SMART_GUILD_PRICE_ID ||
            process.env.REACT_APP_HL_DESIGN_ANNUAL_PRICE_ID,
          tier: 'Guild',
          frequency: 'annually' as const,
          name: 'Smart Guild Marketing Membership',
          audience: 'designer' as const,
          billingComponent: 'annual' as const,
        },
        {
          productId: process.env.REACT_APP_HL_DESIGNER_SMART_GUILD_MONTHLY_PRODUCT_ID,
          priceId: process.env.REACT_APP_HL_DESIGNER_SMART_GUILD_MONTHLY_PRICE_ID,
          tier: 'Guild',
          frequency: 'monthly' as const,
          name: 'Smart Guild Marketing Platform Fee',
          audience: 'designer' as const,
          billingComponent: 'monthly' as const,
        },
        {
          productId: process.env.REACT_APP_HL_HOMEOWNER_BASIC_PRODUCT_ID,
          priceId: process.env.REACT_APP_HL_HOMEOWNER_BASIC_PRICE_ID,
          tier: 'Basic',
          frequency: 'monthly' as const,
          name: 'Homeowner Basic Membership',
          audience: 'homeowner' as const,
        },
        {
          productId: process.env.REACT_APP_HL_HOMEOWNER_GUILD_PRODUCT_ID,
          priceId: process.env.REACT_APP_HL_HOMEOWNER_GUILD_PRICE_ID,
          tier: 'Guild',
          frequency: 'monthly' as const,
          name: 'Homeowner Guild Membership',
          audience: 'homeowner' as const,
        },
        {
          productId: process.env.REACT_APP_HL_HOMEOWNER_GUILD_PLUS_PRODUCT_ID,
          priceId: process.env.REACT_APP_HL_HOMEOWNER_GUILD_PLUS_PRICE_ID,
          tier: 'Guild+',
          frequency: 'annually' as const,
          name: 'Homeowner Guild+ Membership',
          audience: 'homeowner' as const,
        },
      ].filter(config => config.productId);

      if (productConfig.length === 0) {
        setState({ type: "SET_LOADING", payload: false });
        isLoadingPlans.current = false; // Reset loading flag
        return { items: [], total: 0 };
      }

      const transformedPlans = [];
      const fetchedProducts = new Map(); // Cache to avoid duplicate API calls

      const matchesFrequency = (price: any, frequency: string) => {
        const interval = price?.recurring?.interval || price?.recurring?.interval_unit;
        const count = price?.recurring?.interval_count || price?.recurring?.intervalCount;
        const name = (price?.name || '').toLowerCase();
        const containsBi = name.includes('bi-') || name.includes('bi ');
        const containsSemi = name.includes('semi-') || name.includes('semi ');

        if (frequency === 'monthly') {
          return (
            (interval === 'month' && (!count || count === 1)) ||
            name.includes('month')
          );
        }

        if (frequency === 'annually') {
          return (
            (interval === 'year' && (!count || count === 1)) ||
            (interval === 'month' && count === 12) ||
            ((!containsBi && !containsSemi) && (name.includes('annual') || name.includes('year')))
          );
        }

        if (frequency === 'bi-annually') {
          return (
            (interval === 'month' && count === 6) ||
            (interval === 'year' && count === 2) ||
            name.includes('bi-annual') ||
            name.includes('bi annual') ||
            name.includes('semi-annual') ||
            name.includes('semi annual')
          );
        }

        return false;
      };

      const parseMetadataList = (value: unknown): string[] => {
        if (!value) {
          return [];
        }

        if (Array.isArray(value)) {
          return value.filter((item): item is string => typeof item === 'string');
        }

        if (typeof value === 'string') {
          try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) {
              return parsed.filter((item): item is string => typeof item === 'string');
            }
          } catch (error) {
            // Not JSON – treat as newline separated text
            return value
              .split(/\r?\n/)
              .map((item) => item.trim())
              .filter(Boolean);
          }

          return value ? [value] : [];
        }

        return [];
      };

      const shouldNormaliseAsCents = (amount: number): boolean => {
        if (amount < 1000) {
          return false;
        }

        if (amount >= 10000) {
          return true;
        }

        return (amount % 100 === 0) && (amount / 100 >= 100);
      };

      const normaliseCurrencyValue = (amount: number): number => {
        if (shouldNormaliseAsCents(amount)) {
          return amount / 100;
        }

        return amount;
      };

      const adjustForFrequency = (amount: number, frequency?: string): number => {
        if (frequency && frequency !== 'monthly' && amount > 0 && amount < 1000) {
          return amount * 100;
        }
        return amount;
      };

      const reduceCents = (value: number): number => {
        let adjusted = value;
        let guard = 0;

        while (adjusted >= 10000 && guard < 5) {
          adjusted = adjusted / 100;
          guard += 1;
        }

        return adjusted;
      };

      const ensureFrequencyScale = (
        amount: number | undefined,
        frequency?: string,
        tier?: string
      ): number | undefined => {
        if (typeof amount !== 'number' || Number.isNaN(amount)) {
          return undefined;
        }

        let result = amount;

        if (frequency && frequency !== 'monthly' && result > 0 && result < 1000) {
          result = result * 100;
        }

        if (tier === 'Guild+') {
          let guildAdjusted = result;
          let guard = 0;
          while (guildAdjusted >= 1000 && guard < 5) {
            guildAdjusted = guildAdjusted / 100;
            guard += 1;
          }
          return guildAdjusted;
        }

        return reduceCents(result);
      };

      const extractPriceAmount = (priceData: any, frequency?: string, tier?: string): number | undefined => {
        if (!priceData) {
          return undefined;
        }

        const candidate =
          priceData.unit_amount ??
          priceData.unitAmount ??
          priceData.amount ??
          priceData.price ??
          priceData.value;

        if (typeof candidate === 'number') {
          const adjusted = adjustForFrequency(candidate, frequency);
          return ensureFrequencyScale(normaliseCurrencyValue(adjusted), frequency, tier);
        }

        if (typeof candidate === 'string') {
          const parsed = parseFloat(candidate);
          if (!Number.isNaN(parsed)) {
            const adjusted = adjustForFrequency(parsed, frequency);
            return ensureFrequencyScale(normaliseCurrencyValue(adjusted), frequency, tier);
          }
        }

        return undefined;
      };

      const extractOriginalPrice = (priceData: any, frequency: string | undefined, tier: string | undefined, referenceAmount?: number): number | undefined => {
        if (!priceData) {
          return undefined;
        }

        const candidate =
          priceData.compare_at_price ??
          priceData.compareAtPrice ??
          priceData.original_price ??
          priceData.originalPrice;

        let numeric: number | undefined;

        if (typeof candidate === 'number') {
          numeric = adjustForFrequency(candidate, frequency);
        } else if (typeof candidate === 'string') {
          const parsed = parseFloat(candidate);
          if (!Number.isNaN(parsed)) {
            numeric = adjustForFrequency(parsed, frequency);
          }
        }

        if (numeric === undefined) {
          return undefined;
        }

        if (typeof referenceAmount === 'number' && referenceAmount > 0) {
          const ratio = numeric / referenceAmount;
          const scaled = ensureFrequencyScale(normaliseCurrencyValue(numeric), frequency, tier);

          if (ratio > 20) {
            return scaled;
          }

          return ensureFrequencyScale(numeric, frequency, tier);
        }

        return ensureFrequencyScale(normaliseCurrencyValue(numeric), frequency, tier);
      };

      // Fetch each product individually using the correct HighLevel API endpoint
      for (const config of productConfig) {
        const { productId, priceId } = config;
        
        // Skip if productId is undefined
        if (!productId) {
          continue;
        }
        try {
          let product;
          let prices;

          if (fetchedProducts.has(productId)) {
            const cached = fetchedProducts.get(productId);
            product = cached.product;
            prices = cached.prices;
          } else {
            const productUrl = `${state.client.baseUrl}/products/${productId}?locationId=${state.client.locationId}`;

            const productResponse = await fetch(productUrl, {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${state.client.apiKey}`,
                'Content-Type': 'application/json',
                Version: '2021-07-28',
              },
            });

            console.log(`📡 Response status for ${productId}:`, productResponse.status);

            if (!productResponse.ok) {
              const errorText = await productResponse.text();
              console.error(`❌ API Error for product ${productId}:`, {
                status: productResponse.status,
                statusText: productResponse.statusText,
                error: errorText,
                url: productUrl
              });

              if (productResponse.status === 401) {
                console.error('🔐 Authentication failed - JWT token may be expired');
                setState({ type: 'SET_ERROR', payload: new Error('HighLevel API authentication failed. Please check your API key or contact support.') });
                setState({ type: 'SET_LOADING', payload: false });
                isLoadingPlans.current = false;
                return { items: [], total: 0 };
              }

              continue;
            }

            product = await productResponse.json();
            console.log(`📦 Product data for ${productId}:`, product);
            console.log(`🔍 Product ID field check:`, {
              productId,
              productIdField: product.id,
              productIdField2: product._id,
              productIdField3: product.productId,
              allKeys: Object.keys(product)
            });

            const priceUrl = `${state.client.baseUrl}/products/${productId}/price?locationId=${state.client.locationId}`;
            console.log(`💰 Fetching prices from: ${priceUrl}`);

            const priceResponse = await fetch(priceUrl, {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${state.client.apiKey}`,
                'Content-Type': 'application/json',
                Version: '2021-07-28',
              },
            });

            if (priceResponse.ok) {
              const priceData = await priceResponse.json();
              console.log(`💰 Price data for ${product.name}:`, priceData);
              prices = priceData.prices || [];
            } else {
              console.warn(`⚠️ Failed to fetch price for product ${productId}:`, priceResponse.status);
              prices = [];
            }

            fetchedProducts.set(productId, { product, prices });
          }

          const pricesArray = Array.isArray(prices) ? prices : [];

          let price = priceId
            ? pricesArray.find((entry: any) => entry?.id === priceId)
            : undefined;

          if (!price) {
            price = pricesArray.find((entry: any) => matchesFrequency(entry, config.frequency));
          }

          if (!price) {
            price = pricesArray[0];
          }

          const priceAmount = extractPriceAmount(price, config.frequency, config.tier);
          const originalPrice = extractOriginalPrice(price, config.frequency, config.tier, priceAmount);

          console.log('💵 HighLevel pricing', {
            tier: config.tier,
            frequency: config.frequency,
            productId,
            priceId,
            selectedPriceId: price?.id,
            rawAmount: price?.unit_amount,
            priceAmount,
            originalPrice,
            recurring: price?.recurring,
            priceName: price?.name,
          });
          const safeBenefits = parseMetadataList(product?.metadata?.features);
          const safePerks = parseMetadataList(product?.metadata?.perks);
          const productName = product?.name || config.name;
          const productDescription = product?.description || `${config.tier} tier membership with ${config.frequency} billing`;
          const currencyCode = price?.currency || price?.currencyCode || 'USD';

          const transformedPlan = {
            _id: `${productId}-${config.tier.toLowerCase()}-${config.frequency}`,
            slug: `${config.tier.toLowerCase()}-${config.frequency}`,
            name: productName,
            description: productDescription,
            tagLine:
              product?.metadata?.tagLine ||
              `${config.tier} Tier - ${config.frequency.charAt(0).toUpperCase() + config.frequency.slice(1)} Billing`,
            benefits: safeBenefits,
              pricing: {
                price: typeof priceAmount === 'number' ? priceAmount : 0,
                originalPrice:
                  typeof originalPrice === 'number' ? originalPrice : null,
                currency: currencyCode,
                period: config.frequency,
                singlePaymentForDuration: config.frequency !== 'monthly',
                subscription: config.frequency === 'monthly' ? {
                  cycleCount: 1,
                  cycleDuration: {
                  count: 1,
                  unit: 'MONTH' as const
                }
              } : undefined
            },
            public: product.active !== false,
            perks: safePerks,
            primaryPlan: config.tier === 'Guild' && config.frequency === 'annually',
            // HighLevel specific fields
            highlevelProductId: productId as string,
            highlevelPriceId: price?.id,
            metadata: {
              ...(product.metadata || {}),
              tier: config.tier,
              frequency: config.frequency,
              audience: config.audience,
              billingComponent: config.billingComponent || null,
              subscriptionValue:
                config.audience === "designer"
                  ? "Guild"
                  : null,
            }
          };

          console.log(`✅ Transformed plan for ${config.tier} ${config.frequency}:`, transformedPlan);
          transformedPlans.push(transformedPlan);

        } catch (productError) {
          console.error(`❌ Error fetching product ${config.tier} ${config.frequency} (${productId}):`, productError);
          continue; // Skip this product and continue with others
        }
      }

      const designerAnnualPlan = transformedPlans.find(
        (plan: any) =>
          plan?.metadata?.audience === "designer" &&
          plan?.metadata?.billingComponent === "annual"
      );
      const designerMonthlyPlan = transformedPlans.find(
        (plan: any) =>
          plan?.metadata?.audience === "designer" &&
          plan?.metadata?.billingComponent === "monthly"
      );

      const basePlans = transformedPlans.filter(
        (plan: any) => plan?.metadata?.audience !== "designer"
      );

      const outputPlans = [...basePlans];

      if (designerAnnualPlan) {
        const annualAmount = Number(designerAnnualPlan?.pricing?.price ?? 0);
        const monthlyAmount = Number(designerMonthlyPlan?.pricing?.price ?? 0);
        const annualOriginal = Number(designerAnnualPlan?.pricing?.originalPrice ?? 0);
        const monthlyOriginal = Number(designerMonthlyPlan?.pricing?.originalPrice ?? 0);
        const dueNowAmount = Number((annualAmount + monthlyAmount).toFixed(2));
        const annualizedTotalAmount = Number((annualAmount + monthlyAmount * 12).toFixed(2));
        const combinedOriginal = annualOriginal > 0 || monthlyOriginal > 0
          ? Number((annualOriginal + monthlyOriginal * 12).toFixed(2))
          : null;

        outputPlans.unshift({
          ...designerAnnualPlan,
          _id: `${designerAnnualPlan.highlevelProductId}-smart-guild-annually`,
          slug: "smart-guild-marketing-membership",
          name: "Marketing Membership: Smart Guild",
          pricing: {
            ...designerAnnualPlan.pricing,
            price: annualizedTotalAmount,
            originalPrice: combinedOriginal,
            period: "annually",
            singlePaymentForDuration: true,
          },
          metadata: {
            ...(designerAnnualPlan.metadata || {}),
            audience: "designer",
            billingComponent: "bundle",
            bundleType: "smart-guild-marketing",
            subscriptionValue: "Guild",
            annualFeeAmount: annualAmount,
            monthlyPlatformFeeAmount: monthlyAmount,
            dueNowAmount,
            annualizedTotalAmount,
            annualProductId: designerAnnualPlan.highlevelProductId,
            annualPriceId: designerAnnualPlan.highlevelPriceId || null,
            monthlyProductId: designerMonthlyPlan?.highlevelProductId || null,
            monthlyPriceId: designerMonthlyPlan?.highlevelPriceId || null,
          },
        });
      }

      console.log(`🎉 Successfully transformed ${outputPlans.length} plans`);
      console.log('📋 Final transformed plans:', outputPlans.map(p => ({ 
        id: p._id, 
        name: p.name, 
        tier: p.metadata?.tier, 
        frequency: p.metadata?.frequency,
        audience: p.metadata?.audience,
        price: p.pricing?.price 
      })));
      setState({ type: "SET_LOADING", payload: false });
      isLoadingPlans.current = false; // Reset loading flag

      const result = {
        items: outputPlans,
        total: outputPlans.length
      };

      // Cache the result
      planListCache.current = {
        data: result,
        timestamp: Date.now()
      };

      return result;
    } catch (error) {
      console.error('❌ Error fetching HighLevel plans:', error);
      setState({ type: "SET_ERROR", payload: error });
      setState({ type: "SET_LOADING", payload: false });
      isLoadingPlans.current = false; // Reset loading flag
      return { items: [], total: 0 };
    }
  }, [state.client]);

  const clearPlanCache = useCallback(() => {
    planListCache.current = null;
    isLoadingPlans.current = false;
    console.log('🧹 HighLevel: Plan cache cleared manually');

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('highlevel:cacheCleared'));
    }
  }, []);

  const getPlanById = useCallback(async (planId: string): Promise<Plan | null> => {
    if (!state.client) {
      console.error('HighLevel client not initialized');
      return null;
    }

    setState({ type: "SET_LOADING", payload: true });

    try {
      const response = await fetch(
        `${state.client.baseUrl}/products/${planId}?locationId=${state.client.locationId}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${state.client.apiKey}`,
            'Content-Type': 'application/json',
            'Version': '2021-07-28'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch plan: ${response.status}`);
      }

      const product = await response.json();
      
      // Fetch price for the product
      const priceResponse = await fetch(
        `${state.client.baseUrl}/products/${product.id}/price?locationId=${state.client.locationId}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${state.client.apiKey}`,
            'Content-Type': 'application/json',
            'Version': '2021-07-28'
          }
        }
      );

      const priceData = await priceResponse.json();
      const price = priceData.prices?.[0];

      const transformedPlan: Plan = {
        _id: product.id,
        slug: product.metadata?.slug || product.name.toLowerCase().replace(/\s+/g, '-'),
        name: product.name,
        description: product.description || '',
        tagLine: product.metadata?.tagLine || '',
        benefits: product.metadata?.features ? JSON.parse(product.metadata.features) : [],
        pricing: {
          price: price?.unit_amount ? price.unit_amount / 100 : 0,
          originalPrice: product.metadata?.originalPrice ? parseFloat(product.metadata.originalPrice) : null,
          currency: price?.currency || 'USD',
          period: product.metadata?.billingCycle || 'monthly',
          singlePaymentForDuration: product.metadata?.paymentType === 'single',
          subscription: product.metadata?.paymentType === 'subscription' ? {
            cycleCount: parseInt(product.metadata?.cycleCount || '1'),
            cycleDuration: {
              count: parseInt(product.metadata?.cycleDurationCount || '1'),
              unit: product.metadata?.cycleDurationUnit || 'MONTH'
            }
          } : undefined
        },
        public: product.active !== false,
        perks: product.metadata?.perks ? JSON.parse(product.metadata.perks) : [],
        primaryPlan: product.metadata?.primaryPlan === 'true',
        highlevelProductId: product.id,
        highlevelPriceId: price?.id,
        metadata: product.metadata || {}
      };

      setState({ type: "SET_LOADING", payload: false });
      return transformedPlan;
    } catch (error) {
      console.error('Error fetching HighLevel plan:', error);
      setState({ type: "SET_ERROR", payload: error });
      setState({ type: "SET_LOADING", payload: false });
      return null;
    }
  }, [state.client]);

  const createCheckoutSession = useCallback(async (
    planId: string,
    contactId: string,
    successUrl?: string,
    cancelUrl?: string
  ): Promise<string | null> => {
    if (!state.client) {
      console.error('HighLevel client not initialized');
      return null;
    }

    setState({ type: "SET_LOADING", payload: true });

    try {
      // First get the plan details to get the price
      const plan = await getPlanById(planId);
      if (!plan) {
        throw new Error('Plan not found');
      }

      const response = await fetch(
        `${state.client.baseUrl}/payments/orders`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${state.client.apiKey}`,
            'Content-Type': 'application/json',
            'Version': '2021-07-28'
          },
          body: JSON.stringify({
            locationId: state.client.locationId,
            contactId: contactId,
            currency: plan.pricing.currency || 'USD',
            amount: plan.pricing.price * 100, // Convert to cents
            items: [{
              price: plan.highlevelPriceId,
              quantity: 1
            }],
            successUrl: successUrl || `${window.location.origin}/membership/success`,
            cancelUrl: cancelUrl || `${window.location.origin}/membership/cancel`
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to create checkout session: ${response.status}`);
      }

      const data = await response.json();
      setState({ type: "SET_LOADING", payload: false });
      
      return data.checkoutUrl || data.url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setState({ type: "SET_ERROR", payload: error });
      setState({ type: "SET_LOADING", payload: false });
      return null;
    }
  }, [state.client, getPlanById]);

  return (
    <HighLevelContext.Provider
      value={{
        ...state,
        getPlanList,
        getPlanById,
        createCheckoutSession,
        clearPlanCache,
      }}
    >
      {children}
    </HighLevelContext.Provider>
  );
};

// New HighLevel context hook
export const useHighLevelContext = () => {
  const context = useContext(HighLevelContext);

  if (!context) {
    throw new Error("useHighLevelContext must be used within a HighLevelContextProvider");
  }

  return context;
};
